import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { baseURL } from '../api/config';

interface BackfillJob {
  jobId: string;
  fileName: string;
  symbol: string;
  platform: string;
  status: 'Processing' | 'Completed' | 'Failed' | 'Cancelled';
  rowsTotal: number;
  rowsProcessed: number;
  rowsInserted: number;
  rowsFailed: number;
  startedAt: string;
  completedAt?: string;
  progress: number;
  errorMessage?: string;
}

const Backfill = () => {
  const [file, setFile] = useState<File | null>(null);
  const [symbol, setSymbol] = useState('');
  const [platform, setPlatform] = useState('Auto-detect');
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState<BackfillJob[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load existing jobs on mount
  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${baseURL}/api/backfill/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setErrorMessage('');
      } else {
        setErrorMessage('Only CSV files are supported');
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setErrorMessage('');
      } else {
        setErrorMessage('Only CSV files are supported');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage('Please select a CSV file');
      return;
    }

    if (!symbol.trim()) {
      setErrorMessage('Please enter a symbol');
      return;
    }

    setUploading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('symbol', symbol.toUpperCase());
      formData.append('platform', platform);

      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${baseURL}/api/backfill/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      // Add new job to list
      setJobs(prev => [res.data, ...prev]);

      // Start polling for this job
      pollJobStatus(res.data.jobId);

      // Reset form
      setFile(null);
      setSymbol('');
      setPlatform('Auto-detect');
    } catch (err: any) {
      console.error('Upload failed:', err);
      setErrorMessage(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const pollJobStatus = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${baseURL}/api/backfill/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setJobs(prev =>
          prev.map(job => (job.jobId === jobId ? res.data : job))
        );

        // Stop polling when job completes
        if (res.data.status === 'Completed' || res.data.status === 'Failed' || res.data.status === 'Cancelled') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Failed to poll job status:', err);
        clearInterval(interval);
      }
    }, 2000);
  };

  const cancelJob = async (jobId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${baseURL}/api/backfill/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadJobs();
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#10B981';
      case 'Failed': return '#EF4444';
      case 'Cancelled': return '#6B7280';
      default: return '#3B82F6';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return '✓';
      case 'Failed': return '✗';
      case 'Cancelled': return '○';
      default: return '⟳';
    }
  };

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '24px', color: '#F8FAFC' }}>
          Historical Backfill - CSV Upload
        </h1>

        {/* Upload Section */}
        <div style={{
          backgroundColor: '#1E293B',
          padding: '24px',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #334155'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: '#F8FAFC' }}>
            Upload CSV File
          </h2>

          {/* Drag and Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: dragOver ? '2px dashed #3B82F6' : '2px dashed #475569',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              marginBottom: '16px',
              backgroundColor: dragOver ? '#1E3A8A10' : '#0F172A',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>📁</div>
              <div style={{ color: '#94A3B8', fontSize: '14px' }}>
                {file ? (
                  <span style={{ color: '#3B82F6', fontWeight: 500 }}>{file.name}</span>
                ) : (
                  <>
                    Drag and drop your CSV file here, or <span style={{ color: '#3B82F6' }}>click to browse</span>
                  </>
                )}
              </div>
              <div style={{ color: '#64748B', fontSize: '12px', marginTop: '8px' }}>
                Supported format: CSV (max 100MB)
              </div>
            </label>
          </div>

          {/* Platform Selector */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#94A3B8', fontSize: '14px', marginBottom: '8px' }}>
              Platform (CSV Format)
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                backgroundColor: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '6px',
                color: '#F8FAFC',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="Auto-detect">Auto-detect from CSV headers</option>
              <option value="cTrader">cTrader</option>
              <option value="MetaTrader5">MetaTrader 5</option>
              <option value="Dukascopy">Dukascopy</option>
            </select>
            <div style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
              {platform === 'Auto-detect'
                ? 'System will automatically detect the CSV format from headers'
                : `Selected: ${platform} format`}
            </div>
          </div>

          {/* Symbol Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#94A3B8', fontSize: '14px', marginBottom: '8px' }}>
              Symbol (optional - will auto-detect from CSV)
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="e.g., XAUUSD, EURUSD"
              style={{
                width: '100%',
                padding: '10px 14px',
                backgroundColor: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '6px',
                color: '#F8FAFC',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div style={{
              backgroundColor: '#7F1D1D20',
              border: '1px solid #DC2626',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: '#FCA5A5',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: !file || uploading ? '#334155' : '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: !file || uploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload & Process'}
          </button>

          {/* CSV Format Help */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#0F172A',
            borderRadius: '6px',
            border: '1px solid #334155'
          }}>
            <div style={{ color: '#94A3B8', fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>
              Expected CSV Format:
            </div>
            <pre style={{
              color: '#64748B',
              fontSize: '11px',
              margin: 0,
              fontFamily: 'monospace'
            }}>
              {`Symbol,Timestamp,Bid,Ask
XAUUSD,2025-10-26T10:30:00.000Z,2634.50,2634.60
EURUSD,2025-10-26T10:30:01.123Z,1.08450,1.08453`}
            </pre>
          </div>
        </div>

        {/* Jobs Table */}
        <div style={{
          backgroundColor: '#1E293B',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #334155'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: '#F8FAFC' }}>
            Upload History
          </h2>

          {jobs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#64748B',
              fontSize: '14px'
            }}>
              No upload jobs yet. Upload your first CSV file to get started.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155' }}>
                    <th style={tableHeaderStyle}>Status</th>
                    <th style={tableHeaderStyle}>File Name</th>
                    <th style={tableHeaderStyle}>Symbol</th>
                    <th style={tableHeaderStyle}>Platform</th>
                    <th style={tableHeaderStyle}>Progress</th>
                    <th style={tableHeaderStyle}>Rows</th>
                    <th style={tableHeaderStyle}>Inserted</th>
                    <th style={tableHeaderStyle}>Failed</th>
                    <th style={tableHeaderStyle}>Started</th>
                    <th style={tableHeaderStyle}>Duration</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.jobId} style={{ borderBottom: '1px solid #334155' }}>
                      <td style={tableCellStyle}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: getStatusColor(job.status)
                        }}>
                          <span>{getStatusIcon(job.status)}</span>
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>{job.status}</span>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: '#94A3B8' }}>{job.fileName}</span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: '#3B82F6', fontWeight: 600 }}>
                          {job.symbol}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{
                          fontSize: '12px',
                          color: '#94A3B8',
                          backgroundColor: '#334155',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}>
                          {job.platform}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ width: '100%', maxWidth: '120px' }}>
                          <div style={{
                            width: '100%',
                            height: '6px',
                            backgroundColor: '#0F172A',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${job.progress}%`,
                              height: '100%',
                              backgroundColor: getStatusColor(job.status),
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
                            {job.progress}%
                          </div>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: '#94A3B8' }}>
                          {job.rowsTotal.toLocaleString()}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: '#10B981' }}>
                          {job.rowsInserted.toLocaleString()}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: job.rowsFailed > 0 ? '#EF4444' : '#64748B' }}>
                          {job.rowsFailed.toLocaleString()}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '12px', color: '#64748B' }}>
                          {new Date(job.startedAt).toLocaleString()}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ fontSize: '13px', color: '#94A3B8' }}>
                          {formatDuration(job.startedAt, job.completedAt)}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        {job.status === 'Processing' && (
                          <button
                            onClick={() => cancelJob(job.jobId)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#DC2626',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px',
  color: '#94A3B8',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase'
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px',
  color: '#F8FAFC'
};

export default Backfill;
